import React, { useLayoutEffect, useRef, useState } from 'react';
import { EdgeLabelRenderer } from 'reactflow';

const THEME = {
    primary: '#2cbb5d',
    secondary: '#a1a1aa',
    accent: '#7ee8a8',
    active: '#facc15',
    selected: '#38bdf8',
};

function arrowPolygon(x0, y0, tipX, tipY, size = 11) {
    const dx = tipX - x0;
    const dy = tipY - y0;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const bx = tipX - ux * size;
    const by = tipY - uy * size;
    const px = -uy * (size * 0.55);
    const py = ux * (size * 0.55);
    return `${tipX},${tipY} ${bx + px},${by + py} ${bx - px},${by - py}`;
}

export default function MarkovSelfEdge({ id, sourceX, sourceY, label, data, selected }) {
    const pathRef = useRef(null);
    const [arrow, setArrow] = useState(null);

    const state = data?.solutionState || 'off';
    const isActive = state === 'active';
    const isDone = state === 'done';
    const isUpcoming = state === 'upcoming';
    const isPolicy = state === 'policy';
    const isFlow = state === 'flow';

    let stroke = THEME.secondary;
    let strokeWidth = 2.25;
    let opacity = 0.95;
    let className = 'markov-edge';

    if (selected) {
        stroke = THEME.selected;
        strokeWidth = 3;
    } else if (isActive) {
        stroke = THEME.active;
        strokeWidth = 3.5;
        className += ' markov-edge--active';
    } else if (isDone) {
        stroke = THEME.primary;
        strokeWidth = 3;
        className += ' markov-edge--done';
    } else if (isUpcoming) {
        stroke = THEME.primary;
        opacity = 0.35;
    } else if (isPolicy) {
        stroke = THEME.accent;
        strokeWidth = 2.75;
        className += ' markov-edge--policy';
    } else if (isFlow) {
        stroke = THEME.primary;
        className += ' markov-edge--flow';
    } else if (state === 'dim') {
        opacity = 0.18;
    }

    const r = 42;
    const x1 = sourceX + 10;
    const y1 = sourceY;
    const x2 = sourceX - 10;
    const y2 = sourceY;
    const edgePath = `M ${x1} ${y1} C ${x1 + r} ${y1 - r * 1.6}, ${x2 - r} ${y2 - r * 1.6}, ${x2} ${y2}`;
    const labelX = sourceX;
    const labelY = sourceY - r * 1.35;
    const highlight = isActive || isDone || selected;

    useLayoutEffect(() => {
        const el = pathRef.current;
        if (!el) return;
        const len = el.getTotalLength();
        if (!len) return;
        const tip = el.getPointAtLength(len * 0.88);
        const back = el.getPointAtLength(len * 0.76);
        setArrow({ tipX: tip.x, tipY: tip.y, backX: back.x, backY: back.y });
    }, [edgePath]);

    return (
        <g className={className} opacity={opacity}>
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={24}
                className="react-flow__edge-interaction"
            />
            {(isActive || isDone) && (
                <path
                    d={edgePath}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={10}
                    opacity={0.28}
                    className="markov-edge-glow"
                />
            )}
            <path
                ref={pathRef}
                id={id}
                d={edgePath}
                fill="none"
                stroke={stroke}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                className="markov-edge-path"
            />
            {arrow && (
                <polygon
                    points={arrowPolygon(arrow.backX, arrow.backY, arrow.tipX, arrow.tipY, 11)}
                    fill={stroke}
                />
            )}
            {label != null && label !== '' && (
                <EdgeLabelRenderer>
                    <div
                        className={`nodrag nopan markov-edge-label${highlight ? ' markov-edge-label--on' : ''}`}
                        title="Double-click to edit weight"
                        style={{
                            position: 'absolute',
                            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                            pointerEvents: 'all',
                            cursor: 'pointer',
                        }}
                        onDoubleClick={(ev) => {
                            ev.stopPropagation();
                            data?.onEditWeight?.();
                        }}
                    >
                        {label}
                    </div>
                </EdgeLabelRenderer>
            )}
        </g>
    );
}
